/**
 * The login page.
 *
 * Uses the company's own logo file rather than a redrawing of it — the Accounting
 * app reconstructed the mark as SVG and got the paw white when it is really red.
 */

import { useState } from "react";

import { ApiError } from "../../shared/api/client";
import { useI18n } from "../../shared/i18n";
import { LangToggle } from "../../shared/ui/LangToggle";
import { ThemeToggle } from "../../shared/ui/ThemeToggle";
import { useSession } from "./session";

export function LoginPage() {
  const { signIn } = useSession();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password);
    } catch (err) {
      // The server deliberately returns one message for a wrong email and a wrong
      // password, so the form must not add anything that distinguishes them.
      setError(err instanceof ApiError ? err.message : t("login.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="login-top">
        <LangToggle />
        <ThemeToggle />
      </div>

      <main className="login-card">
        <img
          className="login-logo"
          src="/brand/logo.png"
          alt={t("app.name")}
          width={155}
          height={185}
        />
        <h1>{t("login.title")}</h1>
        <p className="login-sub">{t("app.tagline")}</p>

        <form onSubmit={submit} noValidate>
          <label htmlFor="email">{t("login.email")}</label>
          <input
            id="email"
            type="email"
            name="email"
            autoComplete="username"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <label htmlFor="password">{t("login.password")}</label>
          <input
            id="password"
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {/* role="alert" so a screen reader announces the failure instead of
              leaving the user to work out that the form did nothing. */}
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="btn primary" disabled={busy}>
            {busy ? t("login.submitting") : t("login.submit")}
          </button>
        </form>
      </main>

      <footer className="login-foot">{t("app.company")}</footer>
    </div>
  );
}
